import * as Sentry from '@sentry/react'
import { wrapTablesWithDiv } from '@/chat-common/util/source-html-editing-functions'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { convertMarkdownToHtml } from '@/content-viewer/util/markdown-to-html'
import highlightMarkdownHtmlString from '@/content-viewer/util/markdown-reference-highlighter'
import { kGcsUserFilesPipelineBucketRef } from '@/constants/constants-gcs'
import { MimeType } from '@/constants/constants-mime-types'
import { usesStorageEmulator } from '@/firebase/storage-routing'
import { getUserFileViewableUrl, type UserFileViewableUrlResponse } from '../fetch/get-user-file-viewable-url'

type LoadUserFilePdfArgs = {
  userFileId: string
  title?: string
}

type LoadUserFileMarkdownArgs = {
  userFileId: string
  highlightSentences?: string[] | null
}

const MARKDOWN_CONTENT_TYPES = new Set<string>([MimeType.PLAIN_TEXT, MimeType.MARKDOWN, MimeType.HTML])

export async function loadUserFilePdf(args: LoadUserFilePdfArgs): Promise<File> {
  const { userFileId, title } = args
  const { blob, capability } = await fetchUserFileViewableBlob(userFileId)

  if (capability.content_type !== MimeType.PDF) {
    throw new Error(`User-file viewable content is ${capability.content_type}, not ${RenderFormat.PDF}`)
  }

  validateBlobType({
    blobType: blob.type,
    allowedTypes: new Set([MimeType.PDF]),
    errorMessage: 'User-file PDF blob is not application/pdf',
    sentryContext: {
      userFileId,
      title,
      capabilityContentType: capability.content_type,
    },
  })

  return new File([blob], title || `${userFileId}.pdf`, { type: MimeType.PDF })
}

export async function loadUserFileMarkdownHtml(args: LoadUserFileMarkdownArgs): Promise<string> {
  const { userFileId, highlightSentences } = args
  const { blob, capability } = await fetchUserFileViewableBlob(userFileId)

  if (!MARKDOWN_CONTENT_TYPES.has(capability.content_type)) {
    throw new Error(`User-file viewable content is ${capability.content_type}, not ${RenderFormat.MARKDOWN}`)
  }

  validateBlobType({
    blobType: blob.type,
    allowedTypes: MARKDOWN_CONTENT_TYPES,
    errorMessage: `Invalid blob type for user-file markdown fetch: ${blob.type}`,
    sentryContext: {
      userFileId,
      capabilityContentType: capability.content_type,
    },
  })

  let html = await convertMarkdownToHtml(await readBlobText(blob))
  if (highlightSentences?.length) {
    html = await highlightMarkdownHtmlString(html, highlightSentences)
  }
  return wrapTablesWithDiv(html)
}

async function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') {
    return blob.text()
  }
  return new Response(blob).text()
}

async function fetchUserFileViewableBlob(userFileId: string): Promise<{ blob: Blob; capability: UserFileViewableUrlResponse }> {
  const capability = await getUserFileViewableUrl(userFileId)
  const response = await fetch(capability.viewable_url)
  if (!response.ok) {
    throw new Error(`Viewable content fetch failed with status ${response.status}`)
  }
  return {
    blob: await response.blob(),
    capability,
  }
}

function validateBlobType(args: { blobType: string; allowedTypes: Set<string>; errorMessage: string; sentryContext: Record<string, unknown> }) {
  const { blobType, allowedTypes, errorMessage, sentryContext } = args
  if (!blobType || allowedTypes.has(blobType)) return

  const allowEmulatorOctetStream = usesStorageEmulator({ bucketUrl: kGcsUserFilesPipelineBucketRef }) && blobType === MimeType.OCTET_STREAM
  if (allowEmulatorOctetStream) return

  Sentry.captureException(new Error(errorMessage), {
    extra: { ...sentryContext, blobType },
  })
  throw new Error(errorMessage)
}
